/**
 * Maximum Likelihood Estimation for Weibull Distribution with Censored Data
 */

import { FailureRecord } from '@/types/failure';

export interface MLEDataPoint {
  time: number;
  isCensored: boolean;
  failureRecord?: FailureRecord;
}

export interface MLEResult {
  parameters: {
    shape: number; // β (beta)
    scale: number; // η (eta)
    logLikelihood: number;
    convergenceStatus: 'converged' | 'max_iterations' | 'failed';
    iterations: number;
  };
  confidenceIntervals?: {
    shape: { lower: number; upper: number };
    scale: { lower: number; upper: number };
    method?: string; // 'Profile Likelihood' or 'Fisher Information Matrix'
  };
  standardErrors: {
    shape: number;
    scale: number;
  };
  fisherInformation: {
    matrix: number[][];
    determinant: number;
  };
}

export interface MLEConfig {
  maxIterations: number;
  tolerance: number;
  initialValues?: { shape: number; scale: number };
  confidenceLevel: 0.90 | 0.95 | 0.99;
  stepSizeFactor: number;
  parameterBounds?: {
    shape: { min: number; max: number };
    scale: { min: number; max: number };
  };
}

const DEFAULT_CONFIG: MLEConfig = {
  maxIterations: 1000,
  tolerance: 1e-6,
  confidenceLevel: 0.95,
  stepSizeFactor: 1.0,
  parameterBounds: {
    shape: { min: 0.01, max: 20 },
    scale: { min: 0.01, max: 100000 },
  },
};

/**
 * Calculate log-likelihood for Weibull distribution with censored data
 * L(β,η) = Σ[δᵢ·log(f(tᵢ))] + Σ[log(R(tᵢ))]
 * where δᵢ = 1 for failures, 0 for censored observations
 */
export function logLikelihood(shape: number, scale: number, data: MLEDataPoint[]): number {
  if (shape <= 0 || scale <= 0) return -Infinity;
  
  let logL = 0;
  
  for (const point of data) {
    const t = point.time;
    const tOverEta = t / scale;
    const tOverEtaBeta = Math.pow(tOverEta, shape);
    
    if (point.isCensored) {
      // Censored observation: contributes R(t) = exp(-(t/η)^β)
      logL -= tOverEtaBeta;
    } else {
      // Failure observation: contributes f(t) = (β/η)(t/η)^(β-1) * exp(-(t/η)^β)
      logL += Math.log(shape) - Math.log(scale) + (shape - 1) * Math.log(tOverEta) - tOverEtaBeta;
    }
  }
  
  return logL;
}

/**
 * Calculate gradient vector [∂L/∂β, ∂L/∂η] for Newton-Raphson optimization
 */
export function gradient(shape: number, scale: number, data: MLEDataPoint[]): [number, number] {
  if (shape <= 0 || scale <= 0) return [0, 0];
  
  let dL_dBeta = 0;
  let dL_dEta = 0;
  
  for (const point of data) {
    const t = point.time;
    const tOverEta = t / scale;
    const logTOverEta = Math.log(tOverEta);
    const tOverEtaBeta = Math.pow(tOverEta, shape);
    
    if (point.isCensored) {
      // Censored observation gradient contributions
      dL_dBeta -= tOverEtaBeta * logTOverEta;
      dL_dEta += (shape / scale) * tOverEtaBeta;
    } else {
      // Failure observation gradient contributions
      dL_dBeta += (1 / shape) + logTOverEta - tOverEtaBeta * logTOverEta;
      dL_dEta += (-1 / scale) + (shape / scale) * tOverEtaBeta;
    }
  }
  
  return [dL_dBeta, dL_dEta];
}

/**
 * Calculate Hessian matrix [[∂²L/∂β², ∂²L/∂β∂η], [∂²L/∂η∂β, ∂²L/∂η²]]
 * This provides the Fisher Information Matrix for confidence intervals
 */
export function hessian(shape: number, scale: number, data: MLEDataPoint[]): number[][] {
  if (shape <= 0 || scale <= 0) return [[0, 0], [0, 0]];
  
  let d2L_dBeta2 = 0;
  let d2L_dBetaEta = 0;
  let d2L_dEta2 = 0;
  
  for (const point of data) {
    const t = point.time;
    const tOverEta = t / scale;
    const logTOverEta = Math.log(tOverEta);
    const tOverEtaBeta = Math.pow(tOverEta, shape);
    const logTOverEtaSquared = logTOverEta * logTOverEta;
    
    if (point.isCensored) {
      // Censored observation Hessian contributions
      d2L_dBeta2 -= tOverEtaBeta * logTOverEtaSquared;
      d2L_dBetaEta += (1 / scale) * tOverEtaBeta * (shape * logTOverEta - 1);
      d2L_dEta2 -= (shape / (scale * scale)) * tOverEtaBeta * (shape - 1);
    } else {
      // Failure observation Hessian contributions
      d2L_dBeta2 += (-1 / (shape * shape)) - tOverEtaBeta * logTOverEtaSquared;
      d2L_dBetaEta += (1 / scale) * tOverEtaBeta * (shape * logTOverEta - 1);
      d2L_dEta2 += (1 / (scale * scale)) - (shape / (scale * scale)) * tOverEtaBeta * (shape - 1);
    }
  }
  
  return [
    [d2L_dBeta2, d2L_dBetaEta],
    [d2L_dBetaEta, d2L_dEta2]
  ];
}

/**
 * Estimate initial values using method of moments or existing Johnson's method results
 */
export function estimateInitialValues(
  data: MLEDataPoint[], 
  existingEstimate?: { shape: number; scale: number },
  bounds?: { shape: { min: number; max: number }; scale: { min: number; max: number } }
): { shape: number; scale: number } {
  if (existingEstimate && existingEstimate.shape > 0 && existingEstimate.scale > 0) {
    return existingEstimate;
  }
  
  // Simple method of moments estimation for initial values
  const failures = data.filter(p => !p.isCensored);
  if (failures.length === 0) {
    throw new Error('No failure observations for parameter estimation');
  }
  
  const times = failures.map(f => f.time);
  const logTimes = times.map(t => Math.log(t));
  
  const meanLogTime = logTimes.reduce((sum, lt) => sum + lt, 0) / logTimes.length;
  const varLogTime = logTimes.reduce((sum, lt) => sum + Math.pow(lt - meanLogTime, 2), 0) / Math.max(1, logTimes.length - 1);
  
  // Improved initial value estimation
  const cv = Math.sqrt(varLogTime) / meanLogTime;
  let initialShape = 1.0 / cv;
  let initialScale = Math.exp(meanLogTime);
  
  // Apply bounds if provided
  if (bounds) {
    initialShape = Math.max(bounds.shape.min, Math.min(bounds.shape.max, initialShape));
    initialScale = Math.max(bounds.scale.min, Math.min(bounds.scale.max, initialScale));
  } else {
    initialShape = Math.max(0.5, Math.min(5.0, initialShape));
    initialScale = Math.max(1, initialScale);
  }
  
  return { shape: initialShape, scale: initialScale };
}

/**
 * Invert 2x2 matrix for Newton-Raphson step calculation
 */
function invertMatrix2x2(matrix: number[][]): number[][] {
  const [[a, b], [c, d]] = matrix;
  const det = a * d - b * c;
  
  if (Math.abs(det) < 1e-12) {
    throw new Error('Singular Hessian matrix - cannot invert');
  }
  
  return [
    [d / det, -b / det],
    [-c / det, a / det]
  ];
}

/**
 * Calculate matrix-vector multiplication for 2x2 case
 */
function matrixVectorMultiply(matrix: number[][], vector: [number, number]): [number, number] {
  return [
    matrix[0][0] * vector[0] + matrix[0][1] * vector[1],
    matrix[1][0] * vector[0] + matrix[1][1] * vector[1]
  ];
}

/**
 * Main MLE estimation function using Newton-Raphson optimization
 */
export function estimateWeibullMLE(data: MLEDataPoint[], config: Partial<MLEConfig> = {}): MLEResult {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  
  if (data.length < 3) {
    throw new Error('Need at least 3 observations for MLE estimation');
  }
  
  const failures = data.filter(p => !p.isCensored);
  if (failures.length < 2) {
    throw new Error('Need at least 2 failures for parameter estimation');
  }
  
  // Get initial values
  let { shape, scale } = estimateInitialValues(data, cfg.initialValues, cfg.parameterBounds);
  
  let convergenceStatus: 'converged' | 'max_iterations' | 'failed' = 'failed';
  let iterations = 0;
  let stepSizeFactor = cfg.stepSizeFactor;
  
  try {
    let previousLogL = logLikelihood(shape, scale, data);
    
    for (iterations = 0; iterations < cfg.maxIterations; iterations++) {
      // Calculate gradient and Hessian
      const grad = gradient(shape, scale, data);
      const hess = hessian(shape, scale, data);
      
      // Check for convergence
      const gradientNorm = Math.sqrt(grad[0] * grad[0] + grad[1] * grad[1]);
      if (gradientNorm < cfg.tolerance) {
        convergenceStatus = 'converged';
        break;
      }
      
      // Calculate Newton-Raphson step with safety checks and regularization
      let step: [number, number];
      let usingFallback = false;
      try {
        // Check if Hessian needs regularization
        const hessDeterminant = hess[0][0] * hess[1][1] - hess[0][1] * hess[1][0];
        if (Math.abs(hessDeterminant) < 1e-10) {
          const regularization = Math.max(1e-8, Math.abs(hessDeterminant) * 1000);
          hess[0][0] -= regularization;
          hess[1][1] -= regularization;
        }
        
        const hessInv = invertMatrix2x2(hess);
        step = matrixVectorMultiply(hessInv, grad);
      } catch (e) {
        // Use gradient descent if Hessian is still singular
        step = [grad[0], grad[1]];
        stepSizeFactor = Math.min(stepSizeFactor, 0.01);
        usingFallback = true;
      }
      
      // For highly censored data, be more conservative with step sizes
      const censoringRate = data.filter(p => p.isCensored).length / data.length;
      if (censoringRate > 0.8) {
        stepSizeFactor = Math.min(stepSizeFactor, 0.1);
        if (usingFallback) {
          stepSizeFactor = Math.min(stepSizeFactor, 0.05);
        }
      }
      
      // Adaptive step size with line search
      let newShape = shape;
      let newScale = scale;
      let newLogL = -Infinity;
      let currentStepSize = stepSizeFactor;
      
      for (let lineSearch = 0; lineSearch < 10; lineSearch++) {
        const bounds = cfg.parameterBounds!;
        const tryShape = Math.max(bounds.shape.min, Math.min(bounds.shape.max, shape - currentStepSize * step[0]));
        const tryScale = Math.max(bounds.scale.min, Math.min(bounds.scale.max, scale - currentStepSize * step[1]));
        const tryLogL = logLikelihood(tryShape, tryScale, data);
        
        if (tryLogL > previousLogL) {
          newShape = tryShape;
          newScale = tryScale;
          newLogL = tryLogL;
          break;
        }
        
        currentStepSize *= 0.5;
        if (currentStepSize < 1e-8) break;
      }
      
      // Update parameters if improvement found
      if (newLogL > previousLogL) {
        shape = newShape;
        scale = newScale;
        previousLogL = newLogL;
        stepSizeFactor = Math.min(1.0, stepSizeFactor * 1.1);
      } else {
        stepSizeFactor *= 0.5;
        if (stepSizeFactor < 1e-10) {
          break;
        }
      }
    }
    
    if (iterations >= cfg.maxIterations && convergenceStatus !== 'converged') {
      convergenceStatus = 'max_iterations';
    } else if (convergenceStatus === 'failed' && iterations > 0) {
      convergenceStatus = 'max_iterations';
    }
    
  } catch (error) {
    convergenceStatus = 'failed';
  }
  
  // Calculate final statistics
  const finalLogL = logLikelihood(shape, scale, data);
  const finalHessian = hessian(shape, scale, data);
  
  // Fisher Information Matrix is the negative Hessian
  const fisherMatrix = [
    [-finalHessian[0][0], -finalHessian[0][1]],
    [-finalHessian[1][0], -finalHessian[1][1]]
  ];
  
  const fisherDeterminant = fisherMatrix[0][0] * fisherMatrix[1][1] - fisherMatrix[0][1] * fisherMatrix[1][0];
  
  // Calculate confidence intervals using Profile Likelihood method
  // This is more robust for highly censored data than Fisher Information Matrix
  let standardErrors = { shape: 0, scale: 0 };
  let confidenceIntervals;
  
  const censoringRate = data.filter(p => p.isCensored).length / data.length;
  const useProfileLikelihood = censoringRate > 0.3 || fisherDeterminant <= 0;
  
  try {
    if (useProfileLikelihood) {
      const profileCI = profileLikelihoodCI(
        finalLogL,
        shape,
        scale,
        data,
        cfg.confidenceLevel,
        100
      );
      
      confidenceIntervals = {
        shape: profileCI.shape,
        scale: profileCI.scale,
        method: 'Profile Likelihood'
      };
      
      const zCritical = getZCriticalValue(cfg.confidenceLevel);
      standardErrors = {
        shape: (profileCI.shape.upper - profileCI.shape.lower) / (2 * zCritical),
        scale: (profileCI.scale.upper - profileCI.scale.lower) / (2 * zCritical)
      };
      
    } else if (fisherDeterminant > 0) {
      try {
        const covarianceMatrix = invertMatrix2x2(fisherMatrix);
        standardErrors = {
          shape: Math.sqrt(Math.max(0, covarianceMatrix[0][0])),
          scale: Math.sqrt(Math.max(0, covarianceMatrix[1][1]))
        };
        
        if (!isNaN(standardErrors.shape) && !isNaN(standardErrors.scale) &&
            standardErrors.shape > 0 && standardErrors.scale > 0) {
          const zCritical = getZCriticalValue(cfg.confidenceLevel);
          
          confidenceIntervals = {
            shape: {
              lower: Math.max(0.01, shape - zCritical * standardErrors.shape),
              upper: shape + zCritical * standardErrors.shape
            },
            scale: {
              lower: Math.max(0.01, scale - zCritical * standardErrors.scale),
              upper: scale + zCritical * standardErrors.scale
            },
            method: 'Fisher Information Matrix'
          };
        }
      } catch (error) {
        // Fisher matrix inversion failed
      }
    }
  } catch (error) {
    // Could not calculate confidence intervals
  }
  
  return {
    parameters: {
      shape,
      scale,
      logLikelihood: finalLogL,
      convergenceStatus,
      iterations
    },
    confidenceIntervals,
    standardErrors,
    fisherInformation: {
      matrix: fisherMatrix,
      determinant: fisherDeterminant
    }
  };
}

/**
 * Get critical value from standard normal distribution for confidence intervals
 */
function getZCriticalValue(confidenceLevel: number): number {
  switch (confidenceLevel) {
    case 0.90:
      return 1.645;
    case 0.95:
      return 1.96;
    case 0.99:
      return 2.576;
    default:
      return 1.96;
  }
}

/**
 * Convert failure records to MLE data points
 */
export function prepareMLEData(failures: FailureRecord[]): MLEDataPoint[] {
  return failures.map(record => ({
    time: record.operation_time_years,
    isCensored: isCensoredObservation(record),
    failureRecord: record
  })).filter(point => point.time > 0);
}

/**
 * Check if a failure record represents a censored observation
 * (Same logic as existing weibullCensored.ts)
 */
function isCensoredObservation(record: FailureRecord): boolean {
  const zeroUuid = '00000000-0000-0000-0000-000000000000';
  
  return (
    !record.case_id ||
    record.case_id === '' ||
    record.case_id === '0' ||
    record.case_id === 0 ||
    record.failure_id === zeroUuid
  );
}

/**
 * Calculate Profile Likelihood confidence intervals using systematic search
 * Alternative implementation for highly censored Weibull data
 */
export function profileLikelihoodCI(
  maxLogL: number,
  shape: number,
  scale: number,
  data: MLEDataPoint[],
  confidenceLevel: number = 0.95,
  _maxEvals: number = 100
): {
  shape: { lower: number; upper: number };
  scale: { lower: number; upper: number };
  method: string;
} {
  
  // Critical value from chi-squared distribution with 1 DOF
  const chi2Critical = confidenceLevel === 0.99 ? 6.635 : 
                      confidenceLevel === 0.90 ? 2.706 : 3.841; // 95% default
  const logLThreshold = maxLogL - chi2Critical / 2;
  
  // Profile shape parameter bounds (keeping scale fixed)
  const findShapeBounds = (): { lower: number; upper: number } => {
    let shapeLower = shape;
    let shapeUpper = shape;
    
    // Search for lower bound
    for (let i = 1; i <= 50; i++) {
      const tryShape = shape * (1 - i * 0.02);
      if (tryShape <= 0.01) break;
      
      const testLogL = logLikelihood(tryShape, scale, data);
      if (testLogL >= logLThreshold) {
        shapeLower = tryShape;
      } else {
        break;
      }
    }
    
    // Search for upper bound
    for (let i = 1; i <= 50; i++) {
      const tryShape = shape * (1 + i * 0.02);
      if (tryShape >= 20) break;
      
      const testLogL = logLikelihood(tryShape, scale, data);
      if (testLogL >= logLThreshold) {
        shapeUpper = tryShape;
      } else {
        break;
      }
    }
    
    return { lower: shapeLower, upper: shapeUpper };
  };
  
  // Profile scale parameter bounds (keeping shape fixed)
  const findScaleBounds = (): { lower: number; upper: number } => {
    let scaleLower = scale;
    let scaleUpper = scale;
    
    // Search for lower bound
    for (let i = 1; i <= 50; i++) {
      const tryScale = scale * (1 - i * 0.02);
      if (tryScale <= 0.01) break;
      
      const testLogL = logLikelihood(shape, tryScale, data);
      if (testLogL >= logLThreshold) {
        scaleLower = tryScale;
      } else {
        break;
      }
    }
    
    // Search for upper bound
    for (let i = 1; i <= 50; i++) {
      const tryScale = scale * (1 + i * 0.02);
      const testLogL = logLikelihood(shape, tryScale, data);
      if (testLogL >= logLThreshold) {
        scaleUpper = tryScale;
      } else {
        break;
      }
    }
    
    return { lower: scaleLower, upper: scaleUpper };
  };
  
  const shapeBounds = findShapeBounds();
  const scaleBounds = findScaleBounds();
  
  return {
    shape: shapeBounds,
    scale: scaleBounds,
    method: 'Profile Likelihood'
  };
}

/**
 * Validate MLE results for production use
 */
export function validateMLEResults(result: MLEResult): {
  isValid: boolean;
  warnings: string[];
  recommendations: string[];
} {
  const warnings: string[] = [];
  const recommendations: string[] = [];
  let isValid = true;
  
  // Check convergence
  if (result.parameters.convergenceStatus !== 'converged') {
    warnings.push(`Optimization did not converge: ${result.parameters.convergenceStatus}`);
    if (result.parameters.convergenceStatus === 'max_iterations') {
      recommendations.push('Consider increasing maxIterations or improving initial values');
    } else {
      recommendations.push('Consider using Johnson\'s method as fallback');
      isValid = false;
    }
  }
  
  // Check parameter bounds
  if (result.parameters.shape < 0.1 || result.parameters.shape > 20) {
    warnings.push(`Shape parameter ${result.parameters.shape.toFixed(3)} is outside typical range [0.1, 20]`);
  }
  
  if (result.parameters.scale <= 0) {
    warnings.push('Scale parameter must be positive');
    isValid = false;
  }
  
  // Check Fisher Information Matrix validity
  if (result.fisherInformation.determinant <= 0) {
    warnings.push('Fisher Information Matrix is not positive definite');
    recommendations.push('Consider using Profile Likelihood confidence intervals');
  }
  
  // Check confidence interval method and quality
  if (result.confidenceIntervals?.method === 'Profile Likelihood') {
    const shapeWidth = result.confidenceIntervals.shape.upper - result.confidenceIntervals.shape.lower;
    const scaleWidth = result.confidenceIntervals.scale.upper - result.confidenceIntervals.scale.lower;
    const shapeRelativeWidth = shapeWidth / result.parameters.shape;
    const scaleRelativeWidth = scaleWidth / result.parameters.scale;
    
    if (shapeRelativeWidth > 2.0 || scaleRelativeWidth > 2.0) {
      warnings.push('Confidence intervals are very wide (>200% of estimate)');
      recommendations.push('Consider collecting more failure data or using different analysis approach');
    }
  }
  
  // Check standard errors
  if (isNaN(result.standardErrors.shape) || isNaN(result.standardErrors.scale)) {
    warnings.push('Could not calculate standard errors');
    recommendations.push('Consider using larger sample size or different optimization method');
  }
  
  return { isValid, warnings, recommendations };
}